import { Controller } from "@hotwired/stimulus"
import FilterState from "../modules/filter_state"

// Central state controller - single source of truth for all filter state
// Manages URL updates, browser history, and coordinates with UI controllers
// Usage: data-controller="filter-state" data-filter-state-base-path-value="/courses"
export default class extends Controller {
  static values = { 
    basePath: String  // e.g., "/courses"
  }

  connect() {
    // Initialize state from current URL
    const urlParams = new URLSearchParams(window.location.search)
    this.state = FilterState.fromUrlParams(urlParams)
    
    // Store initial state for comparison
    this.initialUrl = window.location.href
    
    // Listen for browser back/forward navigation
    this.boundHandlePopstate = this.handlePopstate.bind(this)
    window.addEventListener("popstate", this.boundHandlePopstate)
    
    // Find the form element for submission
    this.form = this.element.querySelector('form')
  }

  disconnect() {
    window.removeEventListener("popstate", this.boundHandlePopstate)
  }

  // Public API: Update a filter and sync everything
  updateFilter(type, values) {
    const oldState = this.state.clone()
    this.state.setFilter(type, values)
    // Reset to page 1 when filters change
    this.state.resetPage()
    
    // Only proceed if state actually changed
    if (!this.state.equals(oldState)) {
      this._syncUrl()
      this._syncForm()
      this._notifyStateChange()
    }
  }

  // Public API: Update page number
  updatePage(page) {
    const oldState = this.state.clone()
    this.state.setPage(page)
    
    // Only proceed if state actually changed
    if (!this.state.equals(oldState)) {
      this._syncUrl()
      this._syncForm()
      this._notifyStateChange()
    }
  }

  // Public API: Clear a specific filter
  clearFilter(type) {
    this.updateFilter(type, [])
  }

  // Public API: Clear all filters
  async clearAllFilters() {
    // Only proceed if there are actual filters to clear (not just page)
    if (!this.state.hasAnyNonPageFilters()) {
      return // Do nothing if only page parameter exists
    }
    
    const oldState = this.state.clone()
    // Clear filters AND reset to page 1 (new result set should start from beginning)
    this.state.filters.clear()
    this.state.page = 1
    
    if (!this.state.equals(oldState)) {
      this._syncUrl()
      await this._notifyStateChangeAndWait()
      this._syncForm()
    }
  }

  // Public API: Get current filter values for a type
  getFilter(type) {
    return this.state.getFilter(type)
  }

  // Public API: Get all current state
  getState() {
    return this.state.toObject()
  }

  // Handle browser back/forward navigation
  handlePopstate(event) {
    let newState
    
    if (event.state && event.state.filters) {
      // Restore from history state
      newState = new FilterState(event.state.filters)
    } else {
      // Parse from URL (fallback)
      const urlParams = new URLSearchParams(window.location.search)
      newState = FilterState.fromUrlParams(urlParams)
    }
    
    // Update our state and sync form
    this.state = newState
    this._notifyStateChange()
    this._syncForm()
  }

  // Update URL and browser history
  _syncUrl() {
    const queryString = this.state.toUrlParams()
    const newUrl = this.basePathValue + (queryString ? '?' + queryString : '')
    
    // Create history entry with state for restoration
    const historyState = {
      filters: this.state.toObject(),
      timestamp: Date.now()
    }
    
    // Use pushState for filter changes to create navigable history
    window.history.pushState(historyState, '', newUrl)
  }

  // Submit form to update results
  _syncForm() {
    if (this.form) {
      // Build form data from current state (includes page if > 1)
      const formData = this.state.toFormData()
      
      // Set form action to base path (no query params needed)
      this.form.action = this.basePathValue
      
      // Clear existing form inputs and add state-based inputs
      this._updateFormInputs(formData)
      
      // Submit form to update Turbo frame
      this.form.requestSubmit()
    }
  }

  // Update form inputs to match current state
  _updateFormInputs(formData) {
    if (!this.form) return
    
    // Remove any existing dynamic inputs we've added
    this.form.querySelectorAll('input[data-filter-state="dynamic"]').forEach(input => {
      input.remove()
    })
    
    // Add inputs from FormData
    for (const [name, value] of formData.entries()) {
      // Skip if this input already exists in the form (like checkboxes)
      const existingInput = this.form.querySelector(`input[name="${name}"]`)
      if (!existingInput || existingInput.type === 'hidden') {
        // Create or update hidden input
        let input = existingInput
        if (!input) {
          input = document.createElement('input')
          input.type = 'hidden'
          input.name = name
          input.setAttribute('data-filter-state', 'dynamic')
          this.form.appendChild(input)
        }
        input.value = value
      }
    }
  }

  // Notify other controllers of state changes
  _notifyStateChange() {
    this.dispatch("state-changed", { 
      detail: { 
        state: this.state.toObject(),
        filterState: this.state // Pass the actual FilterState instance
      },
      bubbles: true 
    })
  }

  // Notify state change and wait for UI controllers to update
  _notifyStateChangeAndWait() {
    return new Promise((resolve) => {
      // Dispatch the state change event
      this._notifyStateChange()
      
      // Use requestAnimationFrame to wait for the next repaint cycle
      // This ensures all DOM updates from the state change are complete
      requestAnimationFrame(() => {
        resolve()
      })
    })
  }

  // Handle form submission to update state from form data
  handleFormSubmit(event) {
    // Extract state from form data
    const formData = new FormData(this.form)
    const newState = FilterState.fromFormData(formData)
    
    // Update our state (this will trigger URL sync)
    const oldState = this.state.clone()
    this.state = newState
    
    if (!this.state.equals(oldState)) {
      this._syncUrl()
      this._notifyStateChange()
    }
  }

  // Action: Handle pagination clicks
  handlePaginationClick(event) {
    const link = event.target.closest('a[data-turbo-frame]')
    if (!link) return
    
    // Extract page number from pagination link URL
    const url = new URL(link.href)
    const page = url.searchParams.get('page')
    
    if (page) {
      // Use the unified state management
      this.updatePage(page)
    }
  }
}